import { useContext, useEffect, useMemo, useRef } from "react";
import { RoomContext } from "./RoomProvider";
import { useParams } from "react-router-dom";
import api from "../../../hooks/api";
import { useAxios } from "../../../hooks/axios";
import { useConnectionState } from "@livekit/components-react";
import { toast, Id as ToastId } from "react-toastify";

export const useRoom = () => useContext(RoomContext);

export const useRoomId = () => {
  const params = useParams();
  return useMemo(() => {
    return params.id!;
  }, [params.id]);
};

export const useWakeLock = () => {
  const wakeLock = useRef<WakeLockSentinel>();
  const isRequesting = useRef(false);

  useEffect(() => {
    const releaseWakeLock = async () => {
      if (wakeLock.current) {
        await wakeLock.current.release();
        console.debug("Wake lock released");
      }
    };

    const requestWakeLock = async () => {
      if (isRequesting.current || wakeLock.current) {
        // Prevent duplicate requests
        return;
      }
      isRequesting.current = true;

      try {
        if ("wakeLock" in navigator && navigator.wakeLock.request) {
          await releaseWakeLock();
          wakeLock.current = await navigator.wakeLock.request("screen");
          // console.debug("Wake lock acquired");
        } else {
          console.warn("Wake lock is not supported in this browser!");
        }
      } catch (error) {
        console.error("Failed to acquire wake lock!", error);
      } finally {
        isRequesting.current = false;
      }
    };

    requestWakeLock();

    return () => {
      releaseWakeLock();
    };
  }, []);
};

export const usePollRoomMinutesRemaining = () => {
  const roomId = useRoomId();
  const axios = useAxios();
  const getRoom = api.useGetRoom(axios);
  const { setOrganization } = useRoom();

  useEffect(() => {
    const getRoomMinutesInterval = setInterval(async () => {
      const response = await getRoom({ id: roomId });
      setOrganization((prevState) => {
        if (!prevState) {
          return prevState;
        }
        return {
          ...prevState,
          translation_seconds_remaining:
            response.data.organization.translation_seconds_remaining,
        };
      });
    }, 30 * 1000);

    return () => {
      clearInterval(getRoomMinutesInterval);
    };
  }, [getRoom, roomId, setOrganization]);
};

export const useLivekitDisconnectToast = (toastContent: JSX.Element) => {
  const connectionState = useConnectionState();

  const toastRef = useRef<ToastId>();
  return useEffect(() => {
    if (connectionState === "reconnecting" && !toastRef.current) {
      toastRef.current = toast(toastContent, {
        type: "error",
        autoClose: false,
      });
    }
    if (connectionState === "connected" && toastRef.current) {
      toast.dismiss(toastRef.current);
      toastRef.current = undefined;
    }
  }, [connectionState, toastContent]);
};
